import React from 'react';
import { Redirect } from 'react-router-dom';
import {
  Eye,
  Globe,
  Link2,
  Mail,
  MousePointerClick,
  Smartphone,
  TrendingUp,
  Users,
} from 'lucide-react';
import type { LinkInBio, LinkInBioPageSummary } from '@repo/lib';
import { getEnglishLinkInBioLabels } from '@repo/checkout';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PageHeader } from '@dashboard/components/PageHeader';
import Spinner from '@dashboard/components/Spinner';
import StatisticsRangeControls from '@dashboard/components/StatisticsRangeControls';
import useStatisticsRange from '@dashboard/hooks/useStatisticsRange';
import * as KPICard from '@dashboard/scenes/PageOverview/components/KPICard';
import { CountListCard, SeriesTrend } from '@dashboard/scenes/PageOverview/components/PageCharts';
import { useLinkInBio, useLinkInBioAnalytics } from '@dashboard/hooks/useLinkInBio';
import hasLinkInBioLab from '@dashboard/lib/linkInBioLab';
import { blockTitle } from './components/blockLabels';

const labels = getEnglishLinkInBioLabels();

const SOURCE_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  messenger: 'Messenger',
  threads: 'Threads',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  snapchat: 'Snapchat',
  pinterest: 'Pinterest',
  whatsapp: 'WhatsApp',
  x: 'X',
  google: 'Google',
  bing: 'Bing',
  linktree: 'Linktree',
  qr: 'QR code',
  direct: 'Direct or unknown',
};

const DEVICE_NAMES = { mobile: 'Phone', tablet: 'Tablet', desktop: 'Computer' } as const;

const number = new Intl.NumberFormat();
const percent = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 });

type Profile = {
  linkInBio: LinkInBio;
  pages: LinkInBioPageSummary[];
  upcomingEvents: LinkInBioPageSummary[];
};

export function itemTitle(item: string, label: string | null, profile?: Profile): string {
  if (item.startsWith('social:')) {
    const platform = item.slice('social:'.length) as keyof typeof labels.social;
    return labels.social[platform] ?? platform;
  }

  const [blockId, eventId] = item.split(':');
  const block = profile?.linkInBio.blocks.find(candidate => candidate.id === blockId);
  const pages = [...(profile?.pages ?? []), ...(profile?.upcomingEvents ?? [])];

  if (block && eventId) {
    const event = pages.find(page => page.id === eventId);
    return event?.title ?? label ?? 'Event';
  }
  if (block) {
    const page = block.page ? pages.find(candidate => candidate.id === block.page) : undefined;
    return blockTitle(block, page, labels.portal);
  }
  return `${label ?? 'Link'} (removed)`;
}

function TrendCard({
  icon,
  title,
  info,
  value,
  isLoading,
  trend,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  info?: string;
  value: string;
  isLoading: boolean;
  trend?: React.ReactNode;
}) {
  return (
    <KPICard.Container>
      <KPICard.Icon icon={icon} />
      <KPICard.Header>
        <KPICard.Title>{title}</KPICard.Title>
        {info && <KPICard.InfoTooltip>{info}</KPICard.InfoTooltip>}
      </KPICard.Header>
      <KPICard.Value>{isLoading ? <Spinner size="small" /> : value}</KPICard.Value>
      {trend}
    </KPICard.Container>
  );
}

export default function LinkInBioAnalytics() {
  const { seller, isAuthenticating } = useAuth();
  const hasLab = hasLinkInBioLab(seller);
  const statisticsRange = useStatisticsRange({
    storageKey: 'link-in-bio-analytics',
    defaultRange: 'lastFourWeeks',
    bareDefaultUrl: true,
  });
  const { dateStart, dateEnd, unit, timezoneOffset, range } = statisticsRange;
  const isAllTime = range === 'allTime';
  const profile = useLinkInBio({ enabled: hasLab });
  const analytics = useLinkInBioAnalytics(
    {
      // All time starts at the first recorded visit, which only the API knows.
      ...(isAllTime ? {} : { dateStart: dateStart.toISOString() }),
      dateEnd: dateEnd.toISOString(),
      unit,
      timezoneOffset,
    },
    { enabled: hasLab },
  );

  if (!isAuthenticating && seller?._id && !hasLab) {
    return <Redirect to="/" />;
  }

  const data = analytics.data;
  const isLoading = isAuthenticating || !seller?._id || analytics.isPending;
  // The API steps a too-fine unit up (days across years), so the unit select shows what it served.
  const servedUnit = analytics.isPlaceholderData ? unit : (data?.unit ?? unit);
  const totals = data?.totals ?? {
    views: 0,
    visitors: 0,
    clicks: 0,
    viewsWithClicks: 0,
    signups: 0,
  };
  const showSignups =
    totals.signups > 0 || !!profile.data?.linkInBio.blocks.some(block => block.type === 'email');
  const trend = (metric: 'views' | 'visitors' | 'clicks' | 'signups', label: string) => (
    <SeriesTrend
      points={data?.series}
      unit={data?.unit}
      isLoading={isLoading}
      metric={metric}
      label={label}
    />
  );
  const none = (what: string) => (isAllTime ? `No ${what} yet` : `No ${what} in this period`);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        title="Link in bio analytics"
        actions={
          <StatisticsRangeControls statisticsRange={{ ...statisticsRange, unit: servedUnit }} />
        }
      />

      {profile.data && !profile.data.linkInBio.published && (
        <p className="text-sm text-muted-foreground">
          Publish your link in bio, then share your link to see who visits and what they tap.
        </p>
      )}

      {analytics.isError ? (
        <p className="p-16 text-center text-sm text-muted-foreground">
          We could not load your analytics. Refresh the page to try again.
        </p>
      ) : (
        <div className="@container grid gap-2">
          <div
            className={
              showSignups
                ? 'grid gap-2 @md:grid-cols-2 @3xl:grid-cols-3 @6xl:grid-cols-5'
                : 'grid gap-2 @md:grid-cols-2 @3xl:grid-cols-4'
            }
          >
            <TrendCard
              icon={Eye}
              title="Views"
              value={number.format(totals.views)}
              isLoading={isLoading}
              trend={trend('views', 'Views')}
            />
            <TrendCard
              icon={Users}
              title="Visitors"
              info="Counted once per day. Someone who visits on 3 days counts 3 times."
              value={number.format(totals.visitors)}
              isLoading={isLoading}
              trend={trend('visitors', 'Visitors')}
            />
            <TrendCard
              icon={MousePointerClick}
              title="Clicks"
              value={number.format(totals.clicks)}
              isLoading={isLoading}
              trend={trend('clicks', 'Clicks')}
            />
            {showSignups && (
              <TrendCard
                icon={Mail}
                title="Email sign-ups"
                info="New email addresses from your sign-up block. They are saved to your customers."
                value={number.format(totals.signups)}
                isLoading={isLoading}
                trend={trend('signups', 'Email sign-ups')}
              />
            )}
            <TrendCard
              icon={TrendingUp}
              title="Click rate"
              info="Views where someone tapped at least one link."
              value={percent.format(totals.views ? totals.viewsWithClicks / totals.views : 0)}
              isLoading={isLoading}
            />
          </div>
          <div className="grid gap-2 @md:grid-cols-2 @3xl:grid-cols-3">
            <CountListCard
              icon={Link2}
              title="Top links"
              isLoading={isLoading}
              emptyText={none('clicks')}
              rows={(data?.items ?? []).map(row => ({
                key: row.item,
                count: number.format(row.clicks),
                label: itemTitle(row.item, row.label, profile.data),
              }))}
            />
            <CountListCard
              icon={Globe}
              title="Where visitors come from"
              info="WhatsApp and email often show as direct. Add ?utm_source=whatsapp to a link you share to see it by name."
              isLoading={isLoading}
              emptyText={none('views')}
              rows={(data?.sources ?? []).map(row => ({
                key: row.source,
                count: number.format(row.views),
                label: SOURCE_NAMES[row.source] ?? row.source,
              }))}
            />
            <CountListCard
              icon={Smartphone}
              title="Devices"
              isLoading={isLoading}
              emptyText={none('views')}
              rows={(data?.devices ?? []).map(row => ({
                key: row.device,
                count: number.format(row.views),
                label: DEVICE_NAMES[row.device],
              }))}
            />
          </div>
        </div>
      )}
    </div>
  );
}
